import { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Info, CreditCard } from "lucide-react";

import { Loader } from "components/Loader";
import {
  ConfirmDialogContainer,
  Title,
  Body,
  BillingBox,
  Actions,
  PrimaryButton,
  SecondaryButton,
} from "../shared/ConfirmDialog.styles";
import {
  AttestationLabel,
  OrderDetailsContainer,
} from "./PlanConfirmationModal.styles";
import { getPlanShortName, capitalizeWords, formatCurrency } from "utils";
import { BILLING_INTERVAL } from "utils/constants";
import { unixToDateTime, FULL_MONTH_YEAR_FORMAT } from "utils/date";
import { userDetailSelect } from "Layout/slice/selector";
import { usePreviewUpgrade } from "features/Plans/hook/usePreviewUpgrade";

const usd = (n) => {
  const value = Number(n || 0);
  return `${value < 0 ? "-" : ""}${formatCurrency(Math.abs(value))}`;
};

export const PlanConfirmationModal = ({
  show,
  onHide,
  onConfirm,
  plan,
  duration,
  isPaidUpgrade = false,
  isIntervalSwitch = false,
}) => {
  const userDetails = useSelector(userDetailSelect);
  const currentPlan = userDetails?.surmount_plan;
  const card = userDetails?.stripe_subscription?.default_payment_method?.card;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agreed, setAgreed] = useState(false);

  // Proration preview — fetched only while the upgrade modal is open.
  const { preview, loading, isError } = usePreviewUpgrade(
    plan?.id,
    isPaidUpgrade && show
  );

  const handleClose = () => {
    if (isSubmitting) return;
    setAgreed(false);
    onHide();
  };

  const handleConfirm = async () => {
    if (isSubmitting || !agreed) return;
    setIsSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setIsSubmitting(false);
    }
  };

  const shortName = getPlanShortName(plan?.name);

  // -------- Order-details mode (paid -> paid upgrade) --------
  if (isPaidUpgrade) {
    const isYearly = plan?.interval === BILLING_INTERVAL.YEARLY;
    const intervalWord = isYearly ? "year" : "month";
    const intervalAdverb = isYearly ? "yearly" : "monthly";
    const planPrice = Number(plan?.price || 0).toFixed(2);
    const renewalDate =
      preview?.next_renewal &&
      unixToDateTime(preview.next_renewal, FULL_MONTH_YEAR_FORMAT);
    // Same-tier monthly->yearly reads as a billing switch, not a tier upgrade.
    const heading = isIntervalSwitch
      ? `Switch to ${intervalAdverb} billing`
      : `Upgrade to ${shortName}`;

    return (
      <OrderDetailsContainer show={show} onHide={handleClose} backdrop="static">
        <h3 className="upgrade-title">{heading}</h3>

        <div className="plan-cards">
          <div className="plan-card">
            <div className="plan-card-top">
              <span className="current-pill">Current plan</span>
            </div>
            <div>
              <div className="plan-card-name">{currentPlan?.name}</div>
              <div className="plan-card-price">
                ${Number(currentPlan?.price || 0).toFixed(2)}/
                {currentPlan?.interval === BILLING_INTERVAL.YEARLY
                  ? "year"
                  : "month"}{" "}
                + tax
              </div>
            </div>
          </div>
          <div className="plan-card selected">
            <div className="plan-card-top">
              <span className="plan-dot" />
            </div>
            <div>
              <div className="plan-card-name">{plan?.name}</div>
              <div className="plan-card-price">
                ${planPrice}/{intervalWord} + tax
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="preview-loading">
            <Loader />
          </div>
        )}
        {!loading && isError && (
          <div className="preview-error">
            Couldn&apos;t load pricing. Please try again.
          </div>
        )}

        {!loading && !isError && preview && (
          <>
            <div className="order-details">
              <p className="order-heading">Order details</p>

              <div className="order-row">
                <div>
                  <div className="order-row-label">{shortName} plan</div>
                  <div className="order-row-desc">
                    {plan?.benefits?.[0]?.text || "Full plan access"}
                  </div>
                </div>
                <span className="order-row-amount">
                  {usd(preview.plan_amount)}
                </span>
              </div>

              {Number(preview.adjustments) !== 0 && (
                <div className="order-row">
                  <div>
                    <div className="order-row-label">Adjustments</div>
                    <div className="order-row-desc">
                      Unused time on your {getPlanShortName(currentPlan?.name)}{" "}
                      plan, credited to today&apos;s charge
                    </div>
                  </div>
                  <span className="order-row-amount">
                    {usd(preview.adjustments)}
                  </span>
                </div>
              )}

              <div className="order-divider" />

              <div className="order-row">
                <span className="order-row-label">Subtotal</span>
                <span className="order-row-amount">
                  {usd(preview.subtotal)}
                </span>
              </div>
              <div className="order-row">
                <span className="order-row-label">Tax</span>
                <span className="order-row-amount">{usd(preview.tax)}</span>
              </div>

              <div className="order-divider" />

              <div className="order-row total">
                <span className="order-row-label">Total due today</span>
                <span className="order-row-amount">
                  {usd(preview.total_due_today)}
                </span>
              </div>
            </div>

            <div className="renewal-note">
              <Info size={16} className="note-icon" />
              <span>
                Your {shortName} period starts <b>today</b>
                {renewalDate ? <> and renews {renewalDate}</> : null}, then
                auto-renews {intervalAdverb} at ${planPrice}/{intervalWord} +
                tax until canceled.
              </span>
            </div>
          </>
        )}

        <div className="payment-method">
          <span className="pm-label">Payment method</span>
          <div className="pm-right">
            <CreditCard size={18} className="pm-icon" />
            <span>
              {card
                ? `${capitalizeWords(card.brand)} ending in ${card.last4}`
                : "No card on file"}
            </span>
          </div>
        </div>

        <label className="agree">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={isSubmitting}
          />
          <span>
            You agree that Surmount will charge your card in the amount above
            now and on a recurring {intervalAdverb} basis until you cancel in
            accordance with our terms. You can cancel at any time in your
            account settings.
          </span>
        </label>

        <button
          type="button"
          className="upgrade-cta"
          onClick={handleConfirm}
          disabled={!agreed || isSubmitting || loading || isError}
        >
          {isSubmitting && <Loader className="cta-loader" variant="light" />}
          {heading}
        </button>
      </OrderDetailsContainer>
    );
  }

  // -------- Checkout mode (free -> paid: redirect to Stripe Checkout) --------
  const isYearly = duration === BILLING_INTERVAL.YEARLY;
  const intervalLabel = isYearly ? "year" : "month";
  const billingCycle = isYearly ? "Billed annually" : "Billed monthly";

  return (
    <ConfirmDialogContainer show={show} onHide={handleClose} backdrop="static">
      <Title>Upgrade to {shortName}?</Title>

      <Body>
        You&apos;re upgrading to {plan?.name}. You&apos;ll be redirected to our
        secure checkout to complete payment.
      </Body>

      <BillingBox>
        <div className="billing-left">
          <span className="billing-plan">{plan?.name}</span>
          <span className="billing-date">{billingCycle}</span>
        </div>
        <span className="billing-price">
          ${plan?.price} USD / {intervalLabel}
        </span>
      </BillingBox>

      <AttestationLabel>
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          disabled={isSubmitting}
        />
        <span>
          I confirm that I am a U.S. citizen, U.S. permanent resident, or holder
          of a qualifying U.S. visa, with a valid Social Security Number and
          U.S. residential address. I understand that a U.S. brokerage account
          is required to access Surmount investment strategies.
        </span>
      </AttestationLabel>

      <Actions>
        <SecondaryButton
          type="button"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </SecondaryButton>
        <PrimaryButton
          type="button"
          onClick={handleConfirm}
          disabled={isSubmitting || !agreed}
        >
          {isSubmitting && <Loader className="btn-loader" variant="light" />}
          Subscribe
        </PrimaryButton>
      </Actions>
    </ConfirmDialogContainer>
  );
};

PlanConfirmationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  plan: PropTypes.object,
  duration: PropTypes.string,
  isPaidUpgrade: PropTypes.bool,
  isIntervalSwitch: PropTypes.bool,
};
